import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media
 *
 * @extends {Component<Refs>}
 */
class DeferredMedia extends Component {
  /** @type {boolean} */
  isPlaying = false;
  /** @type {boolean} */
  isPreloaded = false;
  /** @type {IntersectionObserver|null} */
  intersectionObserver = null;

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const signal = this.#abortController.signal;
    // If we're to use deferred media for images, we will need to run this only when it's not an image type media
    document.addEventListener(ThemeEvents.mediaStartedPlaying, this.pauseMedia.bind(this), { signal });
    window.addEventListener(DialogCloseEvent.eventName, this.pauseMedia.bind(this), { signal });

    // Set up intelligent preloading for videos in product galleries
    if (this.hasAttribute('data-preload-video') || this.closest('.product-media-container--video, .product-media-container--external_video')) {
      this.setupVideoPreloading();
    }
    
    // Set up hover detection for showing controls
    this.setupHoverControls();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    if (this.hideButtonTimeout) {
      clearTimeout(this.hideButtonTimeout);
    }
  }

  /**
   * Sets up hover controls for video interaction
   */
  setupHoverControls() {
    const signal = this.#abortController.signal;
    
    // Show controls on mouse enter
    this.addEventListener('mouseenter', () => {
      const toggleMediaButton = this.refs.toggleMediaButton;
      if (toggleMediaButton instanceof HTMLElement && this.getAttribute('data-media-loaded')) {
        // Clear any existing hide timeout
        if (this.hideButtonTimeout) {
          clearTimeout(this.hideButtonTimeout);
        }
        
        // Show the button
        toggleMediaButton.classList.remove('hidden');
        
        // If video is playing, hide after delay
        if (this.isPlaying) {
          this.hideButtonTimeout = setTimeout(() => {
            toggleMediaButton.classList.add('hidden');
          }, 2000);
        }
      }
    }, { signal });
    
    // Keep controls visible while hovering over them
    const toggleMediaButton = this.refs.toggleMediaButton;
    if (toggleMediaButton) {
      toggleMediaButton.addEventListener('mouseenter', () => {
        if (this.hideButtonTimeout) {
          clearTimeout(this.hideButtonTimeout);
        }
      }, { signal });
      
      toggleMediaButton.addEventListener('mouseleave', () => {
        if (this.isPlaying) {
          this.hideButtonTimeout = setTimeout(() => {
            toggleMediaButton.classList.add('hidden');
          }, 1500);
        }
      }, { signal });
    }
  }

  /**
   * Sets up intelligent video preloading using Intersection Observer
   */
  setupVideoPreloading() {
    // Use Intersection Observer to preload video when it's near the viewport
    const options = {
      root: null,
      rootMargin: '50% 0px', // Start preloading when video is within 50% of viewport
      threshold: 0
    };

    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.isPreloaded) {
          this.preloadVideo();
        }
      });
    }, options);

    this.intersectionObserver.observe(this);
  }

  /**
   * Preloads video content for smoother playback
   */
  preloadVideo() {
    if (this.isPreloaded || this.getAttribute('data-media-loaded')) return;

    const template = this.querySelector('template');
    if (!template) return;

    // Clone the video element from template
    const content = template.content.firstElementChild?.cloneNode(true);
    if (!content) return;

    // If it's a video element, set it up for preloading
    if (content instanceof HTMLVideoElement) {
      content.preload = 'metadata';
      content.muted = true; // Required for autoplay
      content.playsInline = true;
      
      // Create a hidden container for preloading
      const preloadContainer = document.createElement('div');
      preloadContainer.style.position = 'absolute';
      preloadContainer.style.opacity = '0';
      preloadContainer.style.pointerEvents = 'none';
      preloadContainer.setAttribute('aria-hidden', 'true');
      
      preloadContainer.appendChild(content);
      this.appendChild(preloadContainer);
      
      // Start loading the video
      content.load();
      
      // Mark as preloaded
      this.isPreloaded = true;
      this.setAttribute('data-video-preloaded', 'true');
      
      // If autoplay is enabled, start playing in background
      if (this.hasAttribute('data-autoplay-background')) {
        requestIdleCallback(() => {
          this.playBackgroundVideo();
        });
      }
    } else if (content.tagName === 'IFRAME') {
      // For YouTube/Vimeo, we can't truly preload, but we can prepare the iframe
      this.isPreloaded = true;
      this.setAttribute('data-video-preloaded', 'true');
    }
  }

  /**
   * Plays video silently in the background
   */
  playBackgroundVideo() {
    if (!this.isPreloaded) return;
    
    const video = this.querySelector('video');
    if (video && !this.getAttribute('data-media-loaded')) {
      video.muted = true;
      video.loop = true;
      video.play().catch(() => {
        // Silently fail if autoplay is blocked
      });
      
      // Show the video with fade-in effect
      const container = video.parentElement;
      if (container) {
        container.style.transition = 'opacity 0.5s ease';
        container.style.opacity = '1';
      }
    }
  }

  /**
   * Updates the visual hint for play/pause state
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.refs.toggleMediaButton;
    if (toggleMediaButton instanceof HTMLElement) {
      toggleMediaButton.classList.remove('hidden');
      const playIcon = toggleMediaButton.querySelector('.icon-play');
      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
      const pauseIcon = toggleMediaButton.querySelector('.icon-pause');
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
      
      // Clear any existing timeout
      if (this.hideButtonTimeout) {
        clearTimeout(this.hideButtonTimeout);
      }
      
      // Auto-hide the button after 2 seconds if video is playing
      if (isPlaying) {
        this.hideButtonTimeout = setTimeout(() => {
          toggleMediaButton.classList.add('hidden');
        }, 2000);
      }
    }
  }

  /**
   * Shows the deferred media content
   */
  showDeferredMedia = () => {
    // If video was preloaded, remove the hidden preload container first
    const preloadContainer = this.querySelector('div[aria-hidden="true"]');
    if (preloadContainer && this.isPreloaded) {
      preloadContainer.remove();
    }
    
    this.loadContent(true);
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  };

  /**
   * Loads the content
   * @param {boolean} [focus] - Whether to focus the content
   */
  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) return;

    this.dispatchEvent(new MediaStartedPlayingEvent(this));

    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);

    if (!content) return;

    this.setAttribute('data-media-loaded', 'true');
    this.appendChild(content);

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');

    if (content instanceof HTMLVideoElement && content.getAttribute('autoplay')) {
      // force autoplay for safari
      content.play();
    }
  }

  /**
   * Toggle play/pause state of the media
   */
  toggleMedia() {
    if (this.isPlaying) {
      this.pauseMedia();
    } else {
      this.playMedia();
    }
  }

  playMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*'
      );
    } else {
      this.querySelector('video')?.play();
    }
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  }

  /**
   * Pauses the media
   */
  pauseMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"' + 'pauseVideo' + '","args":""}'
          : '{"method":"pause"}',
        '*'
      );
    } else {
      this.querySelector('video')?.pause();
    }
    this.isPlaying = false;

    // If we've already revealed the deferred media, we should toggle the play/pause hint
    if (this.getAttribute('data-media-loaded')) {
      this.updatePlayPauseHint(this.isPlaying);
    }
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

/**
 * A product model
 */
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
  }

  /**
   * @param {Error[]} errors
   */
  async setupModelViewerUI(errors) {
    if (errors) return;

    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }

    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector('model-viewer');
    if (!element) return;

    const signal = this.#abortController.signal;

    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    // Track pointer events to detect taps
    let pointerStartX = 0;
    let pointerStartY = 0;

    element.addEventListener(
      'pointerdown',
      (/** @type {PointerEvent} */ event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      },
      { signal }
    );

    element.addEventListener(
      'click',
      (/** @type {PointerEvent} */ event) => {
        const distanceX = Math.abs(event.clientX - pointerStartX);
        const distanceY = Math.abs(event.clientY - pointerStartY);
        const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        // Try to ensure that this is a tap, not a drag.
        if (totalDistance < 10) {
          // When the model is paused, it has its own button overlay for playing the model again.
          // If we're receiving a click event, it means the model is playing, all we can do is pause it.
          this.pauseMedia();
        }
      },
      { signal }
    );
  }

  /**
   * Waits for Shopify.ModelViewerUI to be defined.
   * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
   * @returns {Promise<void>}
   */
  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;

    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}
